import { forwardRef, useMemo } from "react";

import { Typography } from "@mui/material";
import { PublicBulletinResource } from "@rubi/api-generated";
import { MediaCard, type MediaCardItem } from "@rubi/ui";

import { useCrimeLabel } from "@/api/loadAlerts";
import { formatDateShort, formatWeekdayShort } from "@/common/utils";
import { getBulletinPriorityColor } from "@/components/alerts/constants";
import { useServiceConfigurations } from "@/hooks/useCommunities";

import { getPublicAlertEvidenceFiles, isRecord } from "./types";

type PublicBulletinCardProps = {
  index: number;
  alertItem: PublicBulletinResource;
  alertClickHandler: (alertItem: PublicBulletinResource, index: number) => void;
  className?: string;
};

const PublicBulletinCard = forwardRef<HTMLDivElement, PublicBulletinCardProps>(
  ({ index, alertItem, alertClickHandler, className }, ref) => {
    const {
      evidence_files,
      occurrence_number,
      type,
      translated_type,
      associated_crime,
      issued_time,
    } = alertItem;
    const occurrenceNumbers = occurrence_number ?? [];
    const alertRecord = alertItem as PublicBulletinResource &
      Record<string, unknown>;
    const issuingPoliceService = isRecord(alertRecord.issuing_police_service)
      ? alertRecord.issuing_police_service
      : undefined;
    const policeServiceName =
      typeof issuingPoliceService?.name === "string"
        ? issuingPoliceService.name
        : undefined;
    const description =
      typeof alertRecord.description === "string"
        ? alertRecord.description
        : "";
    const crimeLabel = useCrimeLabel();
    const serviceConfigurations = useServiceConfigurations();

    const getPoliceServiceName = useMemo(() => {
      if (!policeServiceName) return "";
      const config = serviceConfigurations[policeServiceName];
      return config?.region || config?.shortName || policeServiceName;
    }, [policeServiceName, serviceConfigurations]);

    const occurenceNumber = useMemo(
      () => (occurrenceNumbers.length > 0 ? occurrenceNumbers.join(", ") : ""),
      [occurrenceNumbers],
    );

    const media = useMemo<MediaCardItem[]>(
      () =>
        getPublicAlertEvidenceFiles(evidence_files)
          .sort((a, b) => (a.sort_ordinal ?? 0) - (b.sort_ordinal ?? 0))
          .filter(
            (file) =>
              file.content_type.includes("image") ||
              file.content_type.includes("video"),
          )
          .map((file) =>
            file.content_type.includes("video")
              ? { type: "video", src: file.download_url }
              : {
                  type: "image",
                  src: file.thumbnail_url,
                  // Thumbnails are generated async; fall back to the original.
                  fallbackSrc: file.download_url,
                },
          ),
      [evidence_files],
    );

    const crimeType = (associated_crime ?? [])
      .map((crime) => crimeLabel(crime))
      .join(", ");

    const titleColor = getBulletinPriorityColor(alertItem.priority);

    return (
      <MediaCard
        ref={ref}
        className={`alert-item ${className ?? ""}`}
        onClick={() => alertClickHandler(alertItem, index)}
        title={translated_type ?? type}
        accentColor={titleColor}
        media={media}
        emptyContent={
          <Typography
            variant="body2"
            sx={{ position: "absolute", inset: 0, p: 1.5, overflow: "auto" }}
          >
            {description}
          </Typography>
        }
        footer={
          <>
            {getPoliceServiceName && (
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
                {getPoliceServiceName}
                {occurenceNumber && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ ml: 0.5, fontWeight: 400, color: "text.secondary" }}
                  >
                    - {occurenceNumber}
                  </Typography>
                )}
              </Typography>
            )}
            {issued_time && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {formatDateShort(issued_time)}{" "}
                <i>({formatWeekdayShort(issued_time)})</i>
              </Typography>
            )}
            {crimeType && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  width: "fit-content",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                {crimeType}
              </Typography>
            )}
          </>
        }
      />
    );
  },
);

PublicBulletinCard.displayName = "PublicBulletinCard";

export default PublicBulletinCard;
